import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import api from '../services/api';

/* ───────── animations ───────── */
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ───────── layout ───────── */
const Page = styled.div`
  min-height: 100vh;
  background: #FFFDF7;
  padding: 2rem 1.5rem;
  @media (max-width: 600px) { padding: 1rem; }
`;

const Content = styled.div`
  max-width: 700px;
  margin: 0 auto;
  animation: ${slideUp} 0.5s ease-out;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  color: #9B8B7A;
  font-size: 0.85rem;
  cursor: pointer;
  margin-bottom: 1rem;
  &:hover { color: #DAA520; }
`;

const PageTitle = styled.h1`
  font-size: clamp(1.3rem, 3vw, 1.8rem);
  font-weight: 800;
  margin: 0 0 0.3rem;
  color: #8B6914;
  font-family: 'Playfair Display', Georgia, serif;
`;

const PageSub = styled.p`
  color: #9B8B7A;
  font-size: 0.85rem;
  margin: 0 0 2rem;
`;

/* ───────── profile header ───────── */
const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #FFFFFF;
  border: 1px solid rgba(218,165,32,0.1);
  border-radius: 16px;
`;

const AvatarCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #DAA520, #B8860B);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const ProfileName = styled.div`
  color: #2C1810;
  font-size: 1.1rem;
  font-weight: 700;
`;

const ProfileEmail = styled.div`
  color: #9B8B7A;
  font-size: 0.8rem;
  margin-top: 2px;
`;

const ProfilePhone = styled.div`
  color: #B8A898;
  font-size: 0.75rem;
  margin-top: 2px;
`;

/* ───────── form ───────── */
const FormCard = styled.div`
  background: #FFFFFF;
  border: 1px solid rgba(218,165,32,0.1);
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const FormTitle = styled.h3`
  color: #2C1810;
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Label = styled.label`
  color: #6B5744;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  background: rgba(218,165,32,0.06);
  border: 1px solid rgba(218,165,32,0.15);
  border-radius: 10px;
  padding: 10px 14px;
  color: #2C1810;
  font-size: 0.9rem;
  transition: all 0.3s;
  outline: none;

  &:focus {
    border-color: rgba(218,165,32,0.5);
    box-shadow: 0 0 12px rgba(218,165,32,0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder { color: #B8A898; }
`;

const Select = styled.select`
  background: rgba(218,165,32,0.06);
  border: 1px solid rgba(218,165,32,0.15);
  border-radius: 10px;
  padding: 10px 14px;
  color: #2C1810;
  font-size: 0.9rem;
  transition: all 0.3s;
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: rgba(218,165,32,0.5);
  }

  option {
    background: #FFFFFF;
    color: #2C1810;
  }
`;

const ErrorMsg = styled.div`
  background: rgba(244,67,54,0.15);
  border: 1px solid rgba(244,67,54,0.3);
  border-radius: 10px;
  padding: 10px 14px;
  color: #ef9a9a;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

const SuccessMsg = styled.div`
  background: rgba(76,175,80,0.15);
  border: 1px solid rgba(76,175,80,0.3);
  border-radius: 10px;
  padding: 10px 14px;
  color: #a5d6a7;
  font-size: 0.85rem;
  margin-bottom: 1rem;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Btn = styled.button`
  padding: 10px 24px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
`;

const SaveBtn = styled(Btn)`
  background: linear-gradient(135deg, #DAA520, #B8860B);
  color: #fff;
  &:hover { transform: translateY(-1px); box-shadow: 0 4px 15px rgba(218,165,32,0.3); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const CancelBtn = styled(Btn)`
  background: rgba(218,165,32,0.08);
  color: #6B5744;
  border: 1px solid rgba(218,165,32,0.15);
  &:hover { background: rgba(218,165,32,0.08); color: #2C1810; }
`;

const FieldError = styled.span`
  color: #ef9a9a;
  font-size: 0.72rem;
`;

/* ───────── component ───────── */
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
};

const EditProfile = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    time_of_birth: '',
    place_of_birth: '',
    gender: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Fetch profile on mount
  useEffect(() => {
    api.get('/profile')
      .then(res => {
        const u = res.data?.user || res.data;
        setProfile(u);
        setFormData({
          name: u.name || '',
          email: u.email || '',
          phone: u.phone || '',
          date_of_birth: u.date_of_birth ? new Date(u.date_of_birth).toISOString().split('T')[0] : '',
          time_of_birth: u.time_of_birth ? (() => {
            try { return new Date(u.time_of_birth).toISOString().split('T')[1].slice(0, 5); }
            catch { return ''; }
          })() : '',
          place_of_birth: u.place_of_birth || '',
          gender: u.gender || '',
        });
      })
      .catch(() => setError('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!formData.name) errs.name = 'Name is required';
    if (!formData.phone) errs.phone = 'Phone is required';
    if (!formData.gender) errs.gender = 'Gender is required';
    if (formData.date_of_birth && new Date(formData.date_of_birth) > new Date()) {
      errs.date_of_birth = 'Date of birth cannot be in the future';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const res = await api.put('/profile', formData);
      const u = res.data?.user || res.data;
      setProfile(u);
      // update localStorage
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, name: formData.name, phone: formData.phone }));
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const isNewProfile = profile && !profile.gender && !profile.date_of_birth;

  if (loading) {
    return (
      <Page>
        <Content>
          <div style={{ color: '#9B8B7A', textAlign: 'center', padding: '4rem 0' }}>
            Loading profile...
          </div>
        </Content>
      </Page>
    );
  }

  return (
    <Page>
      <Content>
        <BackBtn onClick={() => navigate('/dashboard')}>← Back to Dashboard</BackBtn>

        <PageTitle>{isNewProfile ? 'Complete Your Profile' : 'My Profile'}</PageTitle>
        <PageSub>
          {isNewProfile
            ? 'Fill in your details for personalized astrology readings'
            : 'Update your personal details and astrology preferences'}
        </PageSub>

        {/* Profile Header */}
        <ProfileHeader>
          <AvatarCircle>{getInitials(formData.name)}</AvatarCircle>
          <ProfileInfo>
            <ProfileName>{formData.name || 'Your Name'}</ProfileName>
            <ProfileEmail>{formData.email}</ProfileEmail>
            {formData.phone && <ProfilePhone>{formData.phone}</ProfilePhone>}
          </ProfileInfo>
        </ProfileHeader>

        {error && <ErrorMsg>{typeof error === 'object' ? error.message || JSON.stringify(error) : error}</ErrorMsg>}
        {success && <SuccessMsg>Profile updated successfully!</SuccessMsg>}

        <form onSubmit={handleSubmit}>
          {/* Basic Info */}
          <FormCard>
            <FormTitle>👤 Basic Information</FormTitle>
            <FormRow>
              <FormGroup>
                <Label>Full Name *</Label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                />
                {fieldErrors.name && <FieldError>{fieldErrors.name}</FieldError>}
              </FormGroup>
              <FormGroup>
                <Label>Email</Label>
                <Input
                  name="email"
                  value={formData.email}
                  disabled
                />
              </FormGroup>
            </FormRow>
            <FormRow>
              <FormGroup>
                <Label>Phone *</Label>
                <Input
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                />
                {fieldErrors.phone && <FieldError>{fieldErrors.phone}</FieldError>}
              </FormGroup>
              <FormGroup>
                <Label>Gender *</Label>
                <Select name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Select>
                {fieldErrors.gender && <FieldError>{fieldErrors.gender}</FieldError>}
              </FormGroup>
            </FormRow>
          </FormCard>

          {/* Birth Details */}
          <FormCard>
            <FormTitle>🌙 Birth Details (for Kundli & Horoscope)</FormTitle>
            <FormRow>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                />
                {fieldErrors.date_of_birth && <FieldError>{fieldErrors.date_of_birth}</FieldError>}
              </FormGroup>
              <FormGroup>
                <Label>Time of Birth</Label>
                <Input
                  type="time"
                  name="time_of_birth"
                  value={formData.time_of_birth}
                  onChange={handleChange}
                />
              </FormGroup>
            </FormRow>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>Place of Birth</Label>
              <Input
                name="place_of_birth"
                value={formData.place_of_birth}
                onChange={handleChange}
                placeholder="City, State, Country"
              />
            </FormGroup>
          </FormCard>

          <BtnRow>
            <CancelBtn type="button" onClick={() => navigate('/dashboard')}>Cancel</CancelBtn>
            <SaveBtn type="submit" disabled={saving}>
              {saving ? 'Saving...' : isNewProfile ? 'Save Profile' : 'Update Profile'}
            </SaveBtn>
          </BtnRow>
        </form>
      </Content>
    </Page>
  );
};

export default EditProfile;
